import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { StoreCountry } from '../models/location.model';
import { StoreRegionService } from '../services/store-region.service';

const DEFAULT_REGION_CODE = 'GLC';
const KNOWN_COUNTRY_CODES = new Set<string>([
  'DZ', 'AD', 'AG', 'AR', 'AU', 'AT', 'BH', 'BD', 'BE', 'BO', 'BR', 'BN',
  'BG', 'BF', 'KH', 'CA', 'CL', 'CN', 'CO', 'CR', 'HR', 'CY', 'CZ', 'DK',
  'TP', 'EC', 'EG', 'SV', 'GQ', 'EE', 'ET', 'EU', 'FI', 'FR', 'DE', 'GLC',
  'GR', 'GP', 'GT', 'HN', 'HK', 'HU', 'IS', 'IN', 'ID', 'IQ', 'IE', 'IT',
  'JP', 'JO', 'KR', 'KW', 'LA', 'LV', 'LB', 'LT', 'LU', 'MO', 'MY', 'MT',
  'MX', 'MA', 'MM', 'NP', 'NL', 'NZ', 'NG', 'OM', 'PK', 'PA', 'PY', 'PE',
  'PH', 'PL', 'PT', 'PR', 'QA', 'RE', 'RO', 'RU', 'SA', 'SL', 'SG', 'SK',
  'SI', 'ZA', 'ES', 'SE', 'CH', 'TW', 'TH', 'TR', 'AE', 'GB', 'US', 'UY',
  'VN'
]);

const getFallbackRegion = (
  storeRegionService: StoreRegionService
): StoreCountry | null => {
  const selectedRegion = storeRegionService.selectedRegion;
  if (selectedRegion) {
    return selectedRegion;
  }

  const countryList = storeRegionService.countryList ?? [];

  return (
    countryList.find((country) => country.is_country_of_request) ??
    countryList.find((country) => country.code === DEFAULT_REGION_CODE) ??
    countryList[0] ??
    null
  );
};

const isKnownCountryCode = (code: string | null): boolean => {
  if (!code) {
    return false;
  }

  return KNOWN_COUNTRY_CODES.has(code.trim().toUpperCase());
};

const isValidCountryCodeParam = (code: string | null): boolean => {
  if (!code) {
    return false;
  }

  const normalizedCode = code.trim().toLowerCase();
  const isGlc = normalizedCode === DEFAULT_REGION_CODE.toLowerCase();
  const hasTwoChars = normalizedCode.length === 2;

  if (!isGlc && !hasTwoChars) {
    return false;
  }

  return isKnownCountryCode(code);
};

const isDefinitelyInvalidCountryCodeFormat = (code: string | null): boolean => {
  if (!code) {
    return false;
  }

  const normalizedCode = code.trim().toLowerCase();
  const isGlc = normalizedCode === DEFAULT_REGION_CODE.toLowerCase();

  return !isGlc && normalizedCode.length !== 2;
};

const getSanitizedHomeQueryParams = (
  queryParams: Record<string, string | string[] | null | undefined>
): Record<string, string | string[] | null | undefined> => {
  const { region, country, ...rest } = queryParams;
  return rest;
};

const buildHomeRedirectTree = (
  router: Router,
  state: RouterStateSnapshot,
  region: StoreCountry
): UrlTree => {
  const parsedUrl = router.parseUrl(state.url);
  const queryParams = parsedUrl.queryParams;

  if (region.has_homepage) {
    return router.createUrlTree(['/', region.code], {
      queryParams: getSanitizedHomeQueryParams(queryParams),
      fragment: parsedUrl.fragment ?? undefined
    });
  }

  return router.createUrlTree(['/'], {
    queryParams: {
      ...getSanitizedHomeQueryParams(queryParams),
      region: region.code
    },
    fragment: parsedUrl.fragment ?? undefined
  });
};

const buildCategoryRedirectTree = (
  router: Router,
  state: RouterStateSnapshot,
  regionCode: string
): UrlTree => {
  const parsedUrl = router.parseUrl(state.url);
  const primarySegments =
    parsedUrl.root.children['primary']?.segments.map((segment) => segment.path) ??
    [];

  if (primarySegments.length >= 2 && primarySegments[0] === 'category') {
    primarySegments[1] = regionCode;
  }

  return router.createUrlTree(['/', ...primarySegments], {
    queryParams: parsedUrl.queryParams,
    fragment: parsedUrl.fragment ?? undefined
  });
};

export const homeRegionUrlGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const storeRegionService = inject(StoreRegionService);

  const pathCountryCode = route.paramMap.get('countryCode');
  const queryRegion =
    route.queryParamMap.get('region') ?? route.queryParamMap.get('country');
  const hasPathCountryCode = pathCountryCode !== null;
  const hasRegionQueryParam = queryRegion !== null;
  const hasDefinitelyInvalidPathCode =
    hasPathCountryCode && isDefinitelyInvalidCountryCodeFormat(pathCountryCode);
  const hasDefinitelyInvalidQueryCode =
    hasRegionQueryParam && isDefinitelyInvalidCountryCodeFormat(queryRegion);

  if (hasDefinitelyInvalidPathCode || hasDefinitelyInvalidQueryCode) {
    const fallbackRegion = getFallbackRegion(storeRegionService);
    if (!fallbackRegion) {
      return true;
    }
    return buildHomeRedirectTree(router, state, fallbackRegion);
  }
  const isPathCodeValid = isValidCountryCodeParam(pathCountryCode);
  const isQueryCodeValid = isValidCountryCodeParam(queryRegion);

  if (
    (!hasPathCountryCode || isPathCodeValid) &&
    (!hasRegionQueryParam || isQueryCodeValid)
  ) {
    return true;
  }

  const fallbackRegion = getFallbackRegion(storeRegionService);
  if (!fallbackRegion) {
    return true;
  }

  return buildHomeRedirectTree(router, state, fallbackRegion);
};

export const categoryRegionUrlGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const storeRegionService = inject(StoreRegionService);

  const pathCountryCode = route.paramMap.get('countryCode');

  if (isDefinitelyInvalidCountryCodeFormat(pathCountryCode)) {
    const fallbackRegion = getFallbackRegion(storeRegionService);
    if (!fallbackRegion) {
      return true;
    }
    return buildCategoryRedirectTree(router, state, fallbackRegion.code);
  }

  if (isValidCountryCodeParam(pathCountryCode)) {
    return true;
  }

  const fallbackRegion = getFallbackRegion(storeRegionService);
  if (!fallbackRegion) {
    return true;
  }

  return buildCategoryRedirectTree(router, state, fallbackRegion.code);
};
